import { ArrowRight, MoveRight, X, Pause } from 'lucide-react';
import { useT } from '../../i18n';
import { formatMoneyShort, formatNumber } from '../../lib/reportFormat';
import { stageLabelKey } from '../../lib/stageLabels';
import type { BaseConversion, ProductType, Stage, StageConversion, StageMetrics } from '../../types/api';
import styles from './FunnelChart.module.css';

const STAGE_CLASS: Record<Stage, string> = {
  VALIDATION: styles.stageValidation,
  VERIFICATION: styles.stageVerification,
  PRIORITIZATION: styles.stagePrioritization,
  REALIZATION: styles.stageRealization,
  INTEGRATOR_SELECTION: styles.stageIntegratorSelection,
  TKP: styles.stageTkp,
  // Issue #80 — «Контракт» reuses the ТКП chip colour for now.
  CONTRACT: styles.stageTkp,
};

export interface FunnelChartProps {
  stages: StageMetrics[];
  conversion: StageConversion[];
  /** Base conversion «Входящие запросы → stage» — rendered as a strip below. */
  baseConversion?: BaseConversion[];
  /** Opens the stage analytics drill-in (dashboard). */
  onStageClick?: (stage: Stage) => void;
  /** Product type of the selected funnel tab — drives the display stage
   * labels (educational route renames two steps, #74). Optional: without it
   * the canonical names are rendered. */
  productType?: ProductType | null;
}

function stageTotal(m: StageMetrics): number {
  const c = m.byConfidence;
  return c.K90 + c.K50 + c.K10 + c.none;
}

/** Conversion % between adjacent stages, rounded to whole percents. */
export function conversionPct(conversion: StageConversion[], from: Stage): number {
  const c = conversion.find((x) => x.from === from);
  return c ? Math.round(c.rate * 100) : 0;
}

/**
 * Value-delivery funnel: 4 stage cards. Primary per stage: big mono
 * «Вошло N / Готово M» (explicit in/out events of the period — they need not
 * reconcile because of returns). Secondary: advanced/dropped/paused counters.
 * Conversion connectors between stages. Pure CSS — no chart lib.
 */
export function FunnelChart({ stages, conversion, baseConversion, onStageClick, productType }: FunnelChartProps) {
  const { t, locale } = useT();

  return (
    <>
      <div className={styles.funnel}>
      {stages.map((m, i) => {
        const total = stageTotal(m);
        const clickable = onStageClick !== undefined;
        return (
        <div className={styles.funnelItem} key={m.stage}>
          <section
            className={`${styles.stage} ${STAGE_CLASS[m.stage]} ${clickable ? styles.stageClickable : ''}`}
            tabIndex={0}
            role={clickable ? 'button' : undefined}
            aria-label={clickable ? t(stageLabelKey(m.stage, productType)) : undefined}
            onClick={clickable ? () => onStageClick(m.stage) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onStageClick(m.stage);
                    }
                  }
                : undefined
            }
          >
            <div className={styles.stageHead}>
              <div className={styles.stageRow}>
                <span className={styles.dot} aria-hidden />
                <span className={styles.stageName} title={t(stageLabelKey(m.stage, productType))}>
                  {t(stageLabelKey(m.stage, productType))}
                </span>
                <span className={styles.stageCount}>{formatNumber(total, locale)}</span>
              </div>
              <span className={styles.stageSum}>
                {total === 0 && m.sum === 0
                  ? t('pipeline.emptyStage')
                  : `≈${formatMoneyShort(m.sum, locale, t('pipeline.millionShort'))} ${t('pipeline.inWork')}`}
              </span>
            </div>
            <div className={styles.stageBody}>
              <div className={styles.flow}>
                <div className={styles.flowIn}>
                  <span className={styles.flowLabel}>{t('pipeline.stageIn')}</span>
                  <b className={styles.flowNum}>+{formatNumber(m.in, locale)}</b>
                </div>
                <div className={styles.flowOut}>
                  <span className={styles.flowLabel}>{t('pipeline.stageOut')}</span>
                  <b className={styles.flowNum}>
                    {formatNumber(m.out, locale)}
                    <MoveRight size={16} aria-hidden />
                  </b>
                </div>
              </div>
              <div className={styles.counts}>
                <div className={`${styles.count} ${styles.countOut}`}>
                  <b>
                    {formatNumber(m.advanced, locale)}
                    <MoveRight size={13} aria-hidden />
                  </b>
                  <span>{t('pipeline.stageAdvanced')}</span>
                </div>
                <div className={`${styles.count} ${styles.countDrop}`}>
                  <b>
                    {formatNumber(m.dropped, locale)}
                    <X size={13} aria-hidden />
                  </b>
                  <span>{t('pipeline.stageDropped')}</span>
                </div>
                <div className={`${styles.count} ${styles.countHold}`}>
                  <b>
                    {formatNumber(m.paused, locale)}
                    <Pause size={12} aria-hidden />
                  </b>
                  <span>{t('pipeline.stagePaused')}</span>
                </div>
              </div>
            </div>
          </section>
          {i < stages.length - 1 && (
            <div className={styles.connector} aria-hidden>
              <span className={styles.conv}>{conversionPct(conversion, m.stage)}%</span>
              <span className={styles.convLabel}>{t('pipeline.conversionLabel')}</span>
              <ArrowRight size={18} />
            </div>
          )}
        </div>
        );
      })}
      </div>
      {baseConversion && baseConversion.length > 0 && (
        <div className={styles.baseConv}>
          <span className={styles.baseConvLabel}>{t('pipeline.baseConversionLabel')}</span>
          {baseConversion
            .filter((b) => b.stage !== 'VALIDATION')
            .map((b) => (
              <span key={b.stage} className={styles.baseConvItem}>
                {t(stageLabelKey(b.stage, productType))} {Math.round(b.rate * 100)}%
              </span>
            ))}
        </div>
      )}
    </>
  );
}
